import { Link } from "react-router-dom";
import { useState } from "react";
import api from "@/lib/api";
import MenuSheet from "@/components/MenuSheet";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, Star, Send, MessageSquareHeart } from "lucide-react";
import { toast } from "sonner";

function StarRating({ value, onChange, size = 8, testIdPrefix = "star" }) {
  const [hover, setHover] = useState(0);
  const display = hover || value;
  return (
    <div className="flex items-center gap-1" role="radiogroup" aria-label="Rating">
      {[1, 2, 3, 4, 5].map((n) => {
        const active = n <= display;
        return (
          <button
            key={n}
            type="button"
            onClick={() => onChange(n)}
            onMouseEnter={() => setHover(n)}
            onMouseLeave={() => setHover(0)}
            data-testid={`${testIdPrefix}-${n}`}
            aria-label={`${n} star${n === 1 ? "" : "s"}`}
            className="transition-transform hover:scale-110 active:scale-95"
          >
            <Star
              strokeWidth={1.5}
              className={`size-${size} ${
                active
                  ? "fill-amber-400 text-amber-500"
                  : "text-muted-foreground/40"
              }`}
            />
          </button>
        );
      })}
    </div>
  );
}

export default function ReviewPage() {
  const [text, setText] = useState("");
  const [rating, setRating] = useState(0);
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (rating === 0) {
      toast.error("Pick a star rating first.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/reviews", { rating, text: text.trim() });
      setSubmitted(true);
      toast.success("Thank you for your review!");
    } catch {
      toast.error("Could not submit review");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen">
      <div className="max-w-2xl mx-auto p-4 sm:p-6 lg:p-8 space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <MenuSheet />
          <Button variant="ghost" size="sm" asChild className="rounded-xl" data-testid="review-back-button">
            <Link to="/">
              <ArrowLeft strokeWidth={1.5} className="size-4 mr-1.5" />
              Back to planner
            </Link>
          </Button>
        </div>

        {/* Title */}
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            <MessageSquareHeart strokeWidth={1.5} className="size-3.5" />
            Leave a review
          </div>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">
            Tell us how Lumivera is treating you
          </h1>
          <p className="text-base text-muted-foreground">
            Your feedback shapes what we build next. Even a sentence helps.
          </p>
        </div>

        {submitted ? (
          <Card className="rounded-2xl" data-testid="review-thanks">
            <CardContent className="pt-8 pb-8 text-center space-y-4">
              <div className="size-14 rounded-2xl bg-amber-100 dark:bg-amber-500/20 mx-auto flex items-center justify-center">
                <Star strokeWidth={1.5} className="size-7 text-amber-500 fill-amber-400" />
              </div>
              <h2 className="font-display text-2xl font-extrabold">Thank you!</h2>
              <p className="text-sm text-muted-foreground max-w-sm mx-auto">
                Your review was received. We read every single one.
              </p>
              <Button
                onClick={() => {
                  setSubmitted(false);
                  setText("");
                  setRating(0);
                }}
                variant="outline"
                className="rounded-xl"
                data-testid="review-write-another"
              >
                Write another review
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card className="rounded-2xl">
            <CardHeader>
              <CardTitle className="text-lg font-display">Your review</CardTitle>
              <CardDescription>
                Share what you love, what's missing, or anything that comes to mind.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={submit} className="space-y-5">
                <div className="space-y-2">
                  <label className="text-sm font-semibold">Your review</label>
                  <Textarea
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    rows={6}
                    placeholder="Type your thoughts here…"
                    className="resize-y text-base"
                    data-testid="review-text-input"
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-semibold block">
                    Your rating
                    {rating > 0 && (
                      <span className="ml-2 text-muted-foreground font-normal">
                        {rating} / 5
                      </span>
                    )}
                  </label>
                  <StarRating value={rating} onChange={setRating} testIdPrefix="review-star" />
                </div>
                <Button
                  type="submit"
                  disabled={busy}
                  className="rounded-xl h-11"
                  data-testid="review-submit-button"
                >
                  <Send strokeWidth={1.5} className="size-4 mr-1.5" />
                  {busy ? "Submitting…" : "Submit review"}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

export { StarRating };
